import { Controller } from '@hotwired/stimulus';
import { buildJsonHeaders } from 'utils/csrf_helpers';

export default class extends Controller {
  static targets = [
    'registration',
    'status',
    'button',
    'flash',
    'checkedCount',
    'checkedHeadingCount',
    'registeredCount',
    'awaitingCount',
    'registrationList',
    'registrationEmpty',
    'checkedList',
    'checkedEmpty',
    'dialDisplay',
    'matchSheet',
    'matchTitle',
    'matchList',
    'matchDismiss',
    'paySheet',
    'payTitle',
    'payQr',
    'payBody',
    'payConfirmed',
    'payConfirmedTitle',
    'payCheckButton',
  ];
  static values = {
    eventId: Number,
    attendanceUrl: String,
    lookupUrl: String,
    paymentQrUrl: String,
    paymentStatusUrl: String,
    // Payment poll: ~1s while they're most likely paying so the checkmark
    // lands right after their phone says paid, then ~4s to spare the server
    pollInterval: { type: Number, default: 1000 },
    slowPollInterval: { type: Number, default: 4000 },
    // Roster refresh cadence — new register & pay signups appear on screen
    rosterInterval: { type: Number, default: 5000 },
  };

  connect() {
    this.flashTimeout = null;
    this.dialDigits = '';
    this.payPollTimer = null;
    this.payAutoCloseTimer = null;
    this.payAttendanceId = null;

    // A walk-in who registers through the QR should see their name appear
    // without anyone touching the kiosk. Skipped while someone is mid-flow.
    this.rosterTimer = setInterval(
      () => this.refreshRoster(),
      this.rosterIntervalValue
    );
  }

  disconnect() {
    if (this.flashTimeout) {
      clearTimeout(this.flashTimeout);
    }
    this.stopPaymentPolling();
    if (this.payAutoCloseTimer) {
      clearTimeout(this.payAutoCloseTimer);
    }
    if (this.rosterTimer) {
      clearInterval(this.rosterTimer);
    }
    if (this.matchAutoDismissTimer) {
      clearTimeout(this.matchAutoDismissTimer);
    }
  }

  // --- Roster refresh (register & pay walk-ins appear on their own) ---

  kioskBusy() {
    const overlayOpen =
      (this.hasMatchSheetTarget && !this.matchSheetTarget.hidden) ||
      (this.hasPaySheetTarget && !this.paySheetTarget.hidden);
    return overlayOpen || this.dialDigits.length > 0;
  }

  async refreshRoster() {
    if (this.kioskBusy()) return;

    try {
      const response = await fetch(window.location.href, {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return;

      const doc = new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );
      if (this.kioskBusy()) return; // someone started typing mid-fetch

      const lists = this.element.querySelector('.event-kiosk__lists');
      const freshLists = doc.querySelector('.event-kiosk__lists');
      if (lists && freshLists) {
        lists.innerHTML = freshLists.innerHTML;
      }

      // Header summary pills
      ['registeredCount', 'checkedCount'].forEach(name => {
        const fresh = doc.querySelector(`[data-event-kiosk-target~="${name}"]`);
        const mine = this.element.querySelector(
          `[data-event-kiosk-target~="${name}"]`
        );
        if (fresh && mine) mine.textContent = fresh.textContent;
      });
    } catch (_) {
      // Offline blip — next tick will try again
    }
  }

  async checkIn(event) {
    const button = event.currentTarget;
    const card = button.closest('[data-registration-id]');

    if (!card || card.dataset.checkedIn === 'true') {
      return;
    }

    const userId = card.dataset.userId;
    if (!userId) {
      this.showMessage(
        'Unable to check in this registration. Please see the front desk.',
        'error'
      );
      return;
    }

    const originalText = button.textContent;
    const originalClass = button.className;
    this.showCheckingState(card, button);

    try {
      const response = await fetch(this.attendanceUrlValue, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          attendance: {
            user_id: userId,
            event_id: this.eventIdValue,
            virtual: false,
          },
        }),
      });

      if (!response.ok) {
        const error = await this.extractError(response);
        throw new Error(error);
      }

      let attendance = null;
      try {
        attendance = await response.json();
      } catch (parseError) {
        console.warn('Unable to parse attendance response', parseError);
      }

      const now = new Date().toISOString();
      attendance ||= {};
      attendance.created_at ||= now;

      await this.moveCardToCheckedList(card, attendance);
      this.updateCheckedCount(1);
      this.updateAwaitingCount(-1);

      this.showMessage(`${card.dataset.userName} is checked in.`, 'success');
    } catch (error) {
      this.resetButtonState(button, originalText, originalClass);
      card?.classList.remove('event-kiosk-card--confirming');
      this.showMessage(
        error.message || 'Unable to check in right now. Please try again.',
        'error'
      );
      return;
    }

    button.disabled = true;
  }

  // --- Dial pad self check-in (account holders only) ---

  // Numbers typed with a leading 1 (1-415-555-0123) get an 11th digit; the
  // server matches on the last 10 either way.
  dialCap() {
    return this.dialDigits.startsWith('1') ? 11 : 10;
  }

  dialDigit(event) {
    if (this.dialDigits.length >= this.dialCap()) return;

    this.dialDigits += event.currentTarget.dataset.digit;
    this.renderDialDisplay();

    // Walk up, type your number, done: look up automatically on the last digit
    if (this.dialDigits.length === this.dialCap()) {
      this.lookupPhone();
    }
  }

  dialBackspace() {
    this.dialDigits = this.dialDigits.slice(0, -1);
    this.renderDialDisplay();
    this.clearPhoneResults();
  }

  dialClear() {
    this.dialDigits = '';
    this.renderDialDisplay();
    this.clearPhoneResults();
  }

  renderDialDisplay() {
    if (!this.hasDialDisplayTarget) return;

    let d = this.dialDigits;
    let prefix = '';
    if (d.startsWith('1')) {
      prefix = '1 ';
      d = d.slice(1);
    }
    let formatted = '';
    if (d.length > 6) {
      formatted = `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    } else if (d.length > 3) {
      formatted = `(${d.slice(0, 3)}) ${d.slice(3)}`;
    } else {
      formatted = d;
    }

    this.dialDisplayTarget.textContent =
      (formatted ? prefix + formatted : prefix.trim()) || ' ';
  }

  async lookupPhone() {
    if (this.dialDigits.length < 10) return;

    try {
      const response = await fetch(this.lookupUrlValue, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({ phone: this.dialDigits }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Unable to look that up right now.');
      }

      if (!data.matches || data.matches.length === 0) {
        this.renderPhoneNotice(
          "We couldn't find that number. Please see the front desk and we'll get you set up."
        );
      } else {
        this.renderPhoneMatches(data.matches);
      }
    } catch (error) {
      this.renderPhoneNotice(
        error.message || 'Unable to look that up right now.'
      );
    }
  }

  // Front and center — the person is standing at the screen waiting for it
  renderPhoneMatches(matches) {
    if (!this.hasMatchSheetTarget) return;

    if (this.hasMatchTitleTarget) {
      this.matchTitleTarget.textContent =
        matches.length === 1 ? 'Is this you?' : 'Which one is you?';
    }

    const list = this.matchListTarget;
    list.innerHTML = '';
    matches.forEach(match => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'event-kiosk__self-match';
      button.textContent = `I'm ${match.label} — check me in`;
      if (match.family_of) {
        // Household hint: a child listed under the parent's phone
        const hint = document.createElement('span');
        hint.className = 'event-kiosk__self-match-family';
        hint.textContent = `family of ${match.family_of}`;
        button.appendChild(hint);
      }
      button.addEventListener('click', () =>
        this.selfCheckIn(match.id, match.label, button)
      );
      list.appendChild(button);
    });

    if (this.hasMatchDismissTarget) {
      // Confirming your name is THE call to action here; backing out is the
      // quiet escape hatch, so the dismiss drops to a small text link.
      this.matchDismissTarget.textContent = 'None of these — go back';
      this.matchDismissTarget.classList.add('event-kiosk__pay-dismiss--quiet');
    }
    this.matchSheetTarget.hidden = false;
    this.armMatchAutoDismiss();
  }

  renderPhoneNotice(message) {
    if (!this.hasMatchSheetTarget) return;

    if (this.hasMatchTitleTarget) {
      this.matchTitleTarget.textContent = 'One moment…';
    }

    const list = this.matchListTarget;
    list.innerHTML = '';
    const notice = document.createElement('div');
    notice.className = 'event-kiosk__self-notice';
    notice.textContent = message;
    list.appendChild(notice);

    if (this.hasMatchDismissTarget) {
      // Notice sheets have no other action, so OK stays a full button.
      this.matchDismissTarget.textContent = 'OK';
      this.matchDismissTarget.classList.remove(
        'event-kiosk__pay-dismiss--quiet'
      );
    }
    this.matchSheetTarget.hidden = false;
    this.armMatchAutoDismiss();
  }

  // A walk-away must never freeze the kiosk: an untouched match overlay
  // clears itself so the roster refresh resumes.
  armMatchAutoDismiss() {
    if (this.matchAutoDismissTimer) clearTimeout(this.matchAutoDismissTimer);
    this.matchAutoDismissTimer = setTimeout(() => this.dismissMatches(), 45000);
  }

  dismissMatches() {
    this.resetPhonePanel();
  }

  async selfCheckIn(userId, label, button) {
    if (button) {
      button.disabled = true;
      button.textContent = 'Checking you in…';
    }

    try {
      const response = await fetch(this.attendanceUrlValue, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          attendance: {
            user_id: userId,
            event_id: this.eventIdValue,
            virtual: false,
          },
        }),
      });

      if (!response.ok) {
        const error = await this.extractError(response);
        throw new Error(
          response.status === 422
            ? "You're already checked in — enjoy class!"
            : error
        );
      }

      let attendance = null;
      try {
        attendance = await response.json();
      } catch (parseError) {
        console.warn('Unable to parse attendance response', parseError);
      }

      // If they were registered, move their existing card; otherwise add one
      const registrationCard = this.hasRegistrationListTarget
        ? this.registrationListTarget.querySelector(
            `[data-user-id="${userId}"]`
          )
        : null;

      if (registrationCard) {
        await this.moveCardToCheckedList(registrationCard, attendance || {});
        this.updateAwaitingCount(-1);
      } else {
        const checkedCard = this.createCheckedCard(attendance, label);
        if (checkedCard && this.hasCheckedListTarget) {
          this.checkedListTarget.prepend(checkedCard);
        }
        this.toggleCheckedEmptyState();
      }

      this.updateCheckedCount(1);
      this.resetPhonePanel();

      // No pass consumed => nothing on the account could pay for this class:
      // offer the personalized payment QR (or pay at the desk)
      const paid = (attendance?.passes || []).length > 0;
      if (paid) {
        this.showMessage(
          `You're checked in, ${label}. Enjoy class!`,
          'success'
        );
      } else {
        await this.showPaySheet(userId, label, attendance?.id);
      }
    } catch (error) {
      this.renderPhoneNotice(
        error.message || 'Unable to check you in. Please see the front desk.'
      );
    }
  }

  async showPaySheet(userId, label, attendanceId = null) {
    try {
      const response = await fetch(this.paymentQrUrlValue, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.qr_svg) {
        throw new Error(data.error || 'no qr');
      }

      if (this.hasPayTitleTarget) {
        this.payTitleTarget.textContent = `You're checked in, ${label}!`;
      }
      if (this.hasPayQrTarget) {
        this.payQrTarget.innerHTML = data.qr_svg;
      }

      // Fresh sheet: QR view visible, confirmation hidden
      if (this.hasPayBodyTarget) this.payBodyTarget.hidden = false;
      if (this.hasPayConfirmedTarget) this.payConfirmedTarget.hidden = true;

      this.payLabel = label;
      this.payAttendanceId = attendanceId || null;
      this.paySheetTarget.hidden = false;
      this.startPaymentPolling();
    } catch (error) {
      // Payment QR unavailable — the check-in still stands; settle at the desk
      this.showMessage(
        `You're checked in, ${label}. Please pay at the front desk.`,
        'success'
      );
    }
  }

  // --- Payment confirmation (poll while the pay sheet is open) ---

  startPaymentPolling() {
    this.stopPaymentPolling();
    if (!this.paymentStatusUrlValue || !this.payAttendanceId) return;

    // Fast for the first 2 minutes, then slow, capped at 6 minutes — the
    // lobby moves on eventually. Each tick waits for the previous check, so
    // requests never pile up on slow wifi.
    const run = {};
    const startedAt = Date.now();
    this.payPollRun = run;

    const tick = async () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed >= 6 * 60 * 1000) {
        // Poll cap reached with no payment: an abandoned sheet must not
        // block the roster refresh forever — return to the ready state.
        this.dismissPay();
        return;
      }

      const paid = await this.checkPaymentStatus();
      if (paid || this.payPollRun !== run) return;

      const delay =
        elapsed < 2 * 60 * 1000
          ? this.pollIntervalValue
          : this.slowPollIntervalValue;
      this.payPollTimer = setTimeout(tick, delay);
    };

    this.payPollTimer = setTimeout(tick, this.pollIntervalValue);
  }

  stopPaymentPolling() {
    this.payPollRun = null;
    if (this.payPollTimer) {
      clearTimeout(this.payPollTimer);
      this.payPollTimer = null;
    }
  }

  // Manual fallback: same check the poller runs, with visible feedback
  async checkPaymentNow(event) {
    const button = event?.currentTarget;
    if (button) {
      button.disabled = true;
      button.textContent = 'Checking…';
    }

    const paid = await this.checkPaymentStatus();

    if (button && !paid) {
      button.textContent = 'No payment yet — try again';
      setTimeout(() => {
        button.disabled = false;
        button.textContent = 'Check payment status';
      }, 2000);
    }
  }

  async checkPaymentStatus() {
    if (!this.paymentStatusUrlValue || !this.payAttendanceId) return false;

    try {
      const url = `${
        this.paymentStatusUrlValue
      }?attendance_id=${encodeURIComponent(this.payAttendanceId)}`;
      const response = await fetch(url, { headers: buildJsonHeaders() });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.missing) {
        // Attendance was undone/no-showed mid-poll — nothing left to watch
        this.stopPaymentPolling();
        return false;
      }
      if (response.ok && data.paid) {
        this.showPayConfirmed();
        return true;
      }
    } catch (_) {
      // Poll again next tick; the manual button is the fallback
    }
    return false;
  }

  showPayConfirmed() {
    this.stopPaymentPolling();

    if (this.hasPayConfirmedTitleTarget) {
      this.payConfirmedTitleTarget.textContent = this.payLabel
        ? `Payment received, ${this.payLabel}!`
        : 'Payment received!';
    }
    if (this.hasPayBodyTarget) this.payBodyTarget.hidden = true;
    if (this.hasPayConfirmedTarget) this.payConfirmedTarget.hidden = false;

    // Lobby kiosk: always return to the ready state on its own
    if (this.payAutoCloseTimer) clearTimeout(this.payAutoCloseTimer);
    this.payAutoCloseTimer = setTimeout(() => this.dismissPay(), 6000);
  }

  dismissPay() {
    this.stopPaymentPolling();
    if (this.payAutoCloseTimer) {
      clearTimeout(this.payAutoCloseTimer);
      this.payAutoCloseTimer = null;
    }
    this.payAttendanceId = null;

    if (this.hasPaySheetTarget) {
      this.paySheetTarget.hidden = true;
    }
    if (this.hasPayQrTarget) {
      this.payQrTarget.innerHTML = '';
    }
    if (this.hasPayBodyTarget) this.payBodyTarget.hidden = false;
    if (this.hasPayConfirmedTarget) this.payConfirmedTarget.hidden = true;
    if (this.hasPayCheckButtonTarget) {
      this.payCheckButtonTarget.disabled = false;
      this.payCheckButtonTarget.textContent = 'Check payment status';
    }
  }

  resetPhonePanel() {
    this.dialDigits = '';
    this.renderDialDisplay();
    this.clearPhoneResults();
  }

  clearPhoneResults() {
    if (this.matchAutoDismissTimer) {
      clearTimeout(this.matchAutoDismissTimer);
      this.matchAutoDismissTimer = null;
    }
    if (this.hasMatchSheetTarget) {
      this.matchSheetTarget.hidden = true;
    }
    if (this.hasMatchListTarget) {
      this.matchListTarget.innerHTML = '';
    }
  }

  updateCheckedCount(delta = 0) {
    if (this.hasCheckedCountTarget) {
      const current = parseInt(this.checkedCountTarget.textContent, 10) || 0;
      const next = Math.max(0, current + delta);
      this.checkedCountTarget.textContent = next;
    }

    if (this.hasCheckedHeadingCountTarget) {
      const current =
        parseInt(this.checkedHeadingCountTarget.textContent, 10) || 0;
      const next = Math.max(0, current + delta);
      this.checkedHeadingCountTarget.textContent = next;
    }
  }

  updateAwaitingCount(delta = 0) {
    const updateTarget = target => {
      if (!target) return;
      const current = parseInt(target.textContent, 10) || 0;
      target.textContent = Math.max(0, current + delta);
    };

    if (this.hasRegisteredCountTarget) {
      updateTarget(this.registeredCountTarget);
    }

    if (this.hasAwaitingCountTarget) {
      updateTarget(this.awaitingCountTarget);
    }
  }

  async moveCardToCheckedList(card, attendance) {
    if (!card) return;

    await this.animateCardOut(card);

    const checkedCard = this.createCheckedCard(
      attendance,
      card.dataset.userName
    );

    if (checkedCard && this.hasCheckedListTarget) {
      this.checkedListTarget.prepend(checkedCard);
    }

    card.remove();
    this.toggleRegistrationEmptyState();
    this.toggleCheckedEmptyState();
  }

  animateCardOut(card) {
    return new Promise(resolve => {
      if (!card) return resolve();

      const handleAnimationEnd = () => {
        card.removeEventListener('animationend', handleAnimationEnd);
        resolve();
      };

      card.addEventListener('animationend', handleAnimationEnd);
      card.classList.add('event-kiosk-card--moving');

      setTimeout(() => {
        card.removeEventListener('animationend', handleAnimationEnd);
        resolve();
      }, 250);
    });
  }

  createCheckedCard(attendance, fallbackName) {
    if (!attendance && !fallbackName) return null;

    const name = this.abbreviatedName(
      attendance?.user?.name || fallbackName || 'Student'
    );
    const card = document.createElement('div');
    card.className =
      'event-kiosk-card event-kiosk-card--checked event-kiosk-card--incoming';
    card.dataset.attendanceId = attendance?.id || '';

    const status = document.createElement('span');
    status.className =
      'event-kiosk-card__status event-kiosk-card__status--checked';
    status.textContent = 'Checked in';

    const footer = document.createElement('div');
    footer.className = 'event-kiosk-card__footer';
    footer.appendChild(status);

    const nameContainer = document.createElement('div');
    nameContainer.className = 'event-kiosk-card__name';
    nameContainer.textContent = name;

    const body = document.createElement('div');
    body.appendChild(nameContainer);

    card.appendChild(body);
    card.appendChild(footer);

    const removeIncoming = () =>
      card.classList.remove('event-kiosk-card--incoming');
    card.addEventListener('animationend', removeIncoming, { once: true });

    return card;
  }

  showCheckingState(card, button) {
    if (card) {
      card.classList.add('event-kiosk-card--confirming');
    }

    if (button) {
      button.disabled = true;
      button.classList.add('event-kiosk-card__button--checked');
      button.textContent = '✓ Checked in';
    }
  }

  abbreviatedName(name) {
    if (!name) return 'Student';

    const trimmed = name.trim();
    const parts = trimmed.split(/\s+/);
    if (parts.length <= 1) return trimmed;

    const lastInitial = parts.pop().charAt(0);
    return `${parts.join(' ')} ${lastInitial}.`;
  }

  resetButtonState(button, text, className) {
    if (!button) return;

    button.disabled = false;
    button.className = className;
    button.textContent = text;
  }

  toggleRegistrationEmptyState() {
    if (!this.hasRegistrationListTarget || !this.hasRegistrationEmptyTarget)
      return;

    if (this.registrationListTarget.childElementCount === 0) {
      this.registrationListTarget.hidden = true;
      this.registrationEmptyTarget.hidden = false;
    } else {
      this.registrationListTarget.hidden = false;
      this.registrationEmptyTarget.hidden = true;
    }
  }

  toggleCheckedEmptyState() {
    if (!this.hasCheckedListTarget || !this.hasCheckedEmptyTarget) return;

    if (this.checkedListTarget.childElementCount === 0) {
      this.checkedEmptyTarget.hidden = false;
    } else {
      this.checkedEmptyTarget.hidden = true;
    }
  }

  showMessage(message, type = 'success') {
    if (!this.hasFlashTarget) return;

    const flash = this.flashTarget;
    flash.textContent = message;
    flash.hidden = false;
    flash.classList.remove(
      'event-kiosk__flash--success',
      'event-kiosk__flash--error'
    );
    flash.classList.add(
      type === 'error'
        ? 'event-kiosk__flash--error'
        : 'event-kiosk__flash--success'
    );

    if (this.flashTimeout) {
      clearTimeout(this.flashTimeout);
    }

    this.flashTimeout = setTimeout(() => {
      flash.hidden = true;
    }, 4000);
  }

  async extractError(response) {
    try {
      const data = await response.json();
      if (data?.errors) {
        if (Array.isArray(data.errors)) {
          return data.errors.join(', ');
        }

        const baseErrors = data.errors.base || data.errors.full_messages;
        if (Array.isArray(baseErrors)) {
          return baseErrors.join(', ');
        }
      }

      if (data?.message) {
        return data.message;
      }
    } catch (error) {
      // Swallow JSON parsing errors and fall back to generic message
    }

    return response.status === 422
      ? 'This student is already checked in.'
      : 'Unable to check in right now. Please try again.';
  }
}
